import React from 'react';
import { FileText, AlertTriangle, Shield, Scale } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - GENMOZPRO"
        description="GENMOZPRO Terms of Service. Read our terms and conditions for using our professional payment testing tools including BIN Generator and Card Validators."
        keywords="terms of service, terms and conditions, genmozpro terms, legal agreement, user agreement"
        canonical="https://genmozpro.com/terms"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-4 sm:mb-6">
            <Scale className="w-3 sm:w-4 h-3 sm:h-4 text-green-500" />
            <span className="text-green-500 text-xs sm:text-sm font-medium">Terms of Service</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            Terms of <span className="text-green-500">Service</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">
            Last updated: {new Date().toLocaleDateString('en-US')}
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm sm:text-base text-gray-300">
              <p className="font-medium text-yellow-400 mb-2">Important Notice</p>
              <p>
                By accessing and using the GENMOZPRO platform, you agree to comply with these Terms of Service. If you do not agree with any part of these terms, do not use our services.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          {/* Section 1 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-300 mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User") and GENMOZPRO ("we", "our" or "Platform"). By accessing or using our services, you confirm that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>You are at least 18 years old</li>
              <li>You have legal capacity to enter into binding contracts</li>
              <li>You have read, understood and agree to be bound by these Terms</li>
              <li>You will comply with all applicable laws and regulations</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">2. Service Description</h2>
            <p className="text-gray-300 mb-4">
              GENMOZPRO provides a professional platform for developers and security researchers to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Generate valid card numbers using Luhn algorithm for testing purposes</li>
              <li>Check test cards in real-time</li>
              <li>Test payment integrations with Stripe Connector</li>
              <li>Access educational articles and technical documentation</li>
            </ul>
            <p className="text-gray-300 font-semibold text-yellow-400">
              IMPORTANT: Our services are intended EXCLUSIVELY for testing, development and educational purposes. Use of real credit card data is strictly prohibited.
            </p>
          </section>

          {/* Section 3 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 flex items-center space-x-2">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
              <span>3. Acceptable Use</span>
            </h2>
            <p className="text-gray-300 mb-4">You agree to use our services only for:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Development and testing of legitimate payment systems</li>
              <li>Educational and security research purposes</li>
              <li>Integration testing in sandbox environments</li>
              <li>Validation of card algorithm implementations</li>
            </ul>
            
            <h3 className="text-lg font-semibold mb-3 text-red-400">Prohibited Use</h3>
            <p className="text-gray-300 mb-2">You MUST NOT use our services to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Test or validate real credit cards without authorization</li>
              <li>Any form of fraud, identity theft or illegal activities</li>
              <li>Violate intellectual property rights</li>
              <li>Distribute malware, viruses or malicious code</li>
              <li>Conduct denial of service (DDoS) attacks</li>
              <li>Attempt unauthorized access to systems</li>
              <li>Resell or redistribute our services without permission</li>
              <li>Use bots, scrapers or unauthorized automation</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">4. User Account and Responsibilities</h2>
            <p className="text-gray-300 mb-4">By creating an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Provide accurate, current and complete information</li>
              <li>Maintain the security of your password and credentials</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be responsible for all activities on your account</li>
              <li>Not share your account with third parties</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">5. Intellectual Property</h2>
            <p className="text-gray-300 mb-4">
              All content, features, functionality and technology of the GENMOZPRO platform are the exclusive property of GENMOZPRO and are protected by copyright, trademark and other intellectual property laws.
            </p>
            <p className="text-gray-300 mb-4">
              You receive a limited, non-exclusive, non-transferable and revocable license to access and use our services for the purposes permitted in these Terms.
            </p>
            <p className="text-gray-300">
              Prohibited: copying, modifying, distributing, selling or commercially exploiting any part of the platform without express written authorization.
            </p>
          </section>

          {/* Section 6 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">6. Advertising and Google AdSense</h2>
            <p className="text-gray-300 mb-4">
              Our site displays ads through Google AdSense. By using our services, you agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Ads may be displayed on various platform pages</li>
              <li>Cookies and tracking technologies may be used to personalize ads</li>
              <li>You will not attempt to defraud, manipulate or interfere with the ad system</li>
              <li>You will not click on ads fraudulently or automatically</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-300 mb-4">
              GENMOZPRO provides its services "as is" and "as available". We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Services will be always available, uninterrupted or error-free</li>
              <li>Results obtained will be accurate or reliable</li>
              <li>Defects will be corrected</li>
            </ul>
            <p className="text-gray-300 mb-4 font-semibold text-yellow-400">
              UNDER NO CIRCUMSTANCES SHALL GENMOZPRO BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, USE OR OTHER INTANGIBLE DAMAGES.
            </p>
          </section>

          {/* Section 8 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">8. Legal Compliance and PCI-DSS</h2>
            <p className="text-gray-300 mb-4">
              Our platform complies with PCI-DSS (Payment Card Industry Data Security Standard) security standards for testing environments. However:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>We do not process, store or transmit real card data</li>
              <li>All generated data is fictitious and for testing only</li>
              <li>You are responsible for ensuring compliance in your own systems</li>
              <li>You must comply with all applicable local, state and federal laws</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">9. Modifications to Terms</h2>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. Significant changes will be notified through the website or email. Your continued use of the platform after changes will constitute your acceptance of the new Terms.
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">10. Termination</h2>
            <p className="text-gray-300 mb-4">
              We may suspend or terminate your account and access to services immediately, without prior notice, for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li>Violation of these Terms of Service</li>
              <li>Improper or abusive use of the platform</li>
              <li>Fraudulent or illegal activities</li>
              <li>Request from government authorities</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">11. Applicable Law and Jurisdiction</h2>
            <p className="text-gray-300">
              These Terms are governed by the laws of Brazil. Any dispute will be resolved in the competent courts of Brazil. If any provision of these Terms is deemed invalid, the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          {/* Section 12 */}
          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">12. Indemnification</h2>
            <p className="text-gray-300">
              You agree to indemnify and hold harmless GENMOZPRO, its directors, employees and agents from any claims, damages, obligations, losses, liabilities, costs or debts resulting from: (a) your use of the platform; (b) violation of these Terms; (c) violation of third party rights.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-green-500/10 border border-green-500/20 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">13. Contact</h2>
            <p className="text-gray-300 mb-4">
              For questions about these Terms of Service:
            </p>
            <div className="space-y-2 text-gray-300">
              <p><strong>Email:</strong> <a href="mailto:legal@genmozpro.com" className="text-green-400 hover:underline">legal@genmozpro.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@genmozpro.com" className="text-green-400 hover:underline">support@genmozpro.com</a></p>
            </div>
          </section>
        </div>
      </div>
      </div>
    </>
  );
};

export default Terms;